import React, { useState } from 'react';
import JustificationContainer from '~/components/atom/JustificationContainer';
import TypographyPair from '../TypographyPair';
import type { Address, Lfa_Conversion_Details } from '~/graphql/types';
import { Label } from '~/components/atom/Typography';
import Service from '../../../Service';
import useOffice from '~/hooks/useOffice';
import useUser from '~/hooks/useUser';
import getAppointmentDateAndTime from '~/components/page/LFA/utils/getDateAndTimeString';
import hasValue from '~/util/hasValue';
import TextButton from '~/components/atom/TextButton';
import AssignOfficeUserModal from '../AssignOfficeUserModal';
import formatAddressAsString from '~/LFA/components/pages/Contact/utils/formatAddressAsString';
import type { ClientLfaConversion } from '~/components/page/LFA/types';

const text = {
  email: 'E-mail',
  phone: 'Telefoon',
  office: 'Vestiging',
  noOffice: 'Geen kantoor toegewezen',
  user: 'Gebruiker',
  noUser: 'Geen gebruiker toegewezen',
  services: 'Diensten',
  appointment: 'Afspraak op',
  appointmentCreated: 'Afspraak gemaakt',
  today: 'Vandaag',
  daysAgo: 'dagen geleden',
  contactAddress: 'Contactadres',
};

type Props = {
  email: string;
  phone: string;
  officeId?: string | null;
  userId?: string | null;
  selectedServices: ClientLfaConversion['selectedServices'];
  appointmentDate: Lfa_Conversion_Details['appointmentDate'];
  createdDaysAgo: number;
  contactId: string;
  name: string;
  address?: Address | null;
  refetch: () => void;
};

const LeadInfoBlock: React.FCC<Props> = ({
  email,
  phone,
  officeId,
  userId,
  selectedServices,
  appointmentDate,
  createdDaysAgo,
  contactId,
  name,
  address,
  refetch,
}) => {
  const [isAssignOfficeUserModalOpen, setIsAssignOfficeUserModalOpen] =
    useState(false);
  const office = useOffice(officeId);
  const user = useUser(userId);
  const { date, time } = getAppointmentDateAndTime(appointmentDate);

  return (
    <>
      {isAssignOfficeUserModalOpen && (
        <AssignOfficeUserModal
          onClose={() => setIsAssignOfficeUserModalOpen(false)}
          contactId={contactId}
          name={name}
          refetch={refetch}
          officeId={officeId}
          userId={userId}
        />
      )}
      <JustificationContainer
        width="100%"
        margin={['m', null]}
        justification="space-between"
      >
        <JustificationContainer direction="column" gap="base">
          <TypographyPair label={text.email} value={email} />
          <TypographyPair label={text.phone} value={phone} />
          <JustificationContainer align="center" gap="xs">
            <TypographyPair
              label={text.office}
              value={hasValue(office?.name) ? office.name : text.noOffice}
            />
            <TextButton
              padding={[null]}
              appearance="secondary"
              icon="edit"
              onClick={() => setIsAssignOfficeUserModalOpen(true)}
            />
          </JustificationContainer>
          <JustificationContainer align="center" gap="xs">
            <TypographyPair
              label={text.user}
              value={
                user && 'name' in user && hasValue(user?.name)
                  ? user.name
                  : text.noUser
              }
            />
            <TextButton
              padding={[null]}
              appearance="secondary"
              icon="edit"
              onClick={() => setIsAssignOfficeUserModalOpen(true)}
            />
          </JustificationContainer>
          <JustificationContainer align="center">
            <Label size="s" margin={[null]} width="80px">
              {text.services}
            </Label>
            <JustificationContainer gap="xxs" align="center">
              {selectedServices.map(service => (
                <Service key={service.id} label={service.name} />
              ))}
            </JustificationContainer>
          </JustificationContainer>
          {address && (
            <TypographyPair
              label={text.contactAddress}
              value={formatAddressAsString(address)}
            />
          )}
        </JustificationContainer>
        <JustificationContainer direction="column" gap="xxs" align="end">
          <TypographyPair
            label={text.appointment}
            value={`${date} ${time ? `om ${time}` : ''}`}
            justificationProps={{ direction: 'column', width: '100%' }}
            labelWidth="100%"
          />
          <TypographyPair
            label={text.appointmentCreated}
            value={
              createdDaysAgo === 0
                ? text.today
                : `${createdDaysAgo} ${text.daysAgo}`
            }
            justificationProps={{ direction: 'column', width: '100%' }}
            labelWidth="100%"
          />
        </JustificationContainer>
      </JustificationContainer>
    </>
  );
};

export default LeadInfoBlock;
